<template>
  <div class="jump-page">
    <div class="jump-panel">
      <div v-if="loading" class="spinner"></div>
      <div v-else class="status-icon">!</div>
      <h1>{{ title }}</h1>
      <p>{{ message }}</p>
      <button v-if="showCopy" type="button" @click="copyLink">复制链接</button>
    </div>
  </div>
</template>

<script>
import { getPayOrderInfo } from '@/api/api'
import config from '@/config'
import wayCodeUtils from '@/utils/wayCode'

export default {
  data() {
    return {
      loading: true,
      title: '正在进入收银台',
      message: '请稍候',
      showCopy: false,
      loadingDelay: 0,
      loadingStartedAt: 0,
      loadingTimer: null
    }
  },

  mounted() {
    this.loadingStartedAt = Date.now()
    this.loadingDelay = this.getLoadingDelay()
    this.dispatch()
  },

  beforeDestroy() {
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer)
    }
  },

  methods: {
    dispatch() {
      if (wayCodeUtils.getPayWay()) {
        this.goTransfer('hub')
        return
      }

      getPayOrderInfo('', false).then(orderInfo => {
        const supportWayCodes = Array.isArray(orderInfo.supportWayCodes) ? orderInfo.supportWayCodes : []

        if (this.canUseWebCashier(supportWayCodes)) {
          this.title = '正在进入收银台'
          this.message = '正在匹配可用的网页支付方式'
          this.goTransfer('webpay', true)
          return
        }

        if (this.canUseHostedQrcode(orderInfo, supportWayCodes)) {
          this.title = '正在生成支付二维码'
          this.message = '当前浏览器暂不可直接支付，正在打开二维码支付页'
          this.goQrcodePage(orderInfo)
          return
        }

        this.showOpenInApp('当前商户暂未开通网页支付，请复制链接到微信或支付宝内打开')
      }).catch(res => {
        this.loading = false
        this.title = '无法进入收银台'
        this.message = res && res.msg ? res.msg : '请重新扫码或联系商户'
      })
    },

    goTransfer(target, replace = false) {
      this.runAfterLoading(() => {
        const route = {
          name: 'TransferConfirm',
          params: { jeepayToken: this.$route.params.jeepayToken },
          query: { target }
        }
        if (replace) {
          this.$router.replace(route)
        } else {
          this.$router.push(route)
        }
      })
    },

    getLoadingDelay() {
      return 1000 + Math.floor(Math.random() * 501)
    },

    runAfterLoading(callback) {
      if (this.loadingTimer) {
        clearTimeout(this.loadingTimer)
      }

      const elapsed = Date.now() - this.loadingStartedAt
      const waitTime = Math.max(0, this.loadingDelay - elapsed)
      this.loadingTimer = setTimeout(callback, waitTime)
    },

    canUseWebCashier(supportWayCodes) {
      const mobileWays = [
        config.payWay.WXPAY_H5.wayCode,
        config.payWay.ALIPAY_WAP.wayCode,
        config.payWay.EXCLUSIVE.wayCode,
        'UPACP_WAP'
      ]
      const qrWays = [
        config.payWay.WXPAY_NATIVE.wayCode,
        config.payWay.ALIPAY_PC.wayCode,
        config.payWay.ALIPAY_QR.wayCode,
        config.payWay.ALIPAY_OC.wayCode,
        config.payWay.EXCLUSIVE.wayCode,
        'UPACP_PC',
        'UPACP_QR'
      ]
      const candidates = wayCodeUtils.isMobile() ? mobileWays : qrWays
      return candidates.some(wayCode => supportWayCodes.indexOf(wayCode) >= 0)
    },

    canUseHostedQrcode(orderInfo, supportWayCodes) {
      if (!orderInfo || !orderInfo.payOrderId) {
        return false
      }
      const scanWays = [
        config.payWay.WXPAY.wayCode,
        config.payWay.WXPAY_NATIVE.wayCode,
        config.payWay.ALIPAY.wayCode,
        config.payWay.ALIPAY_QR.wayCode,
        config.payWay.ALIPAY_OC.wayCode,
        config.payWay.EXCLUSIVE.wayCode,
        'UPACP_QR'
      ]
      return scanWays.some(wayCode => supportWayCodes.indexOf(wayCode) >= 0)
    },

    goQrcodePage(orderInfo) {
      this.runAfterLoading(() => {
        location.replace(orderInfo.qrcodePageUrl || ('/jump.html?token=' + encodeURIComponent(this.$route.params.jeepayToken)))
      })
    },

    showOpenInApp(message) {
      this.loading = false
      this.title = '请在微信或支付宝内打开'
      this.message = message
      this.showCopy = true
    },

    copyLink() {
      const text = location.href
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.message = '链接已复制，请粘贴到微信或支付宝内打开'
        })
        return
      }

      const input = document.createElement('input')
      input.value = text
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      this.message = '链接已复制，请粘贴到微信或支付宝内打开'
    }
  }
}
</script>

<style lang="css" scoped>
.jump-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  box-sizing: border-box;
  background: #f5f7fb;
  color: #1f2733;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif;
}

.jump-panel {
  width: 100%;
  max-width: 560px;
  padding: 54px 42px;
  box-sizing: border-box;
  border-radius: 22px;
  background: #fff;
  text-align: center;
  box-shadow: 0 20px 55px rgba(25, 42, 70, 0.08);
}

.spinner {
  width: 76px;
  height: 76px;
  margin: 0 auto 30px;
  border: 7px solid #dbe6f7;
  border-top-color: #2f80ed;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.status-icon {
  width: 76px;
  height: 76px;
  margin: 0 auto 30px;
  border-radius: 50%;
  background: #f06464;
  color: #fff;
  font-size: 48px;
  line-height: 76px;
  font-weight: 700;
}

h1 {
  margin: 0;
  font-size: 34px;
  font-weight: 700;
}

p {
  margin: 22px 0 0;
  color: #657083;
  font-size: 26px;
  line-height: 1.55;
}

button {
  width: 100%;
  height: 82px;
  margin-top: 34px;
  border: 0;
  border-radius: 12px;
  background: #2f80ed;
  color: #fff;
  font-size: 28px;
  font-weight: 600;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
